import QuestionService from '@/api/question'
import { BetRuleService } from '@/api/bet'
import constants from '@/constants'
import { getPositionLabels } from '@/features/market/utils/positionLabels'

/**
 * Check if an outcome is clickable
 */
export const isClickable = (outcome, question, parentQuestion, currencyId) => {
  const q = question || parentQuestion

  if (outcome.disabled) {
    return false
  }
  if (q.is_wagerable === false) {
    return false
  }

  if (QuestionService.isWaitingForResults(q)) {
    return false
  }

  if (q.resolution != null) {
    return false
  }

  if (outcome.price && outcome.price[currencyId] === undefined) {
    return false
  }

  return true
}

/**
 * Whether one side of a mono market is the side that won. The two sides share a single
 * physical outcome and the market carries no `resolution` of its own, so the winner is
 * `resolution_display` read against that side's label.
 */
export const sideMatchesResolutionDisplay = (outcome, question, position) => {
  if (!outcome?.resolution_display) return false

  const labels = getPositionLabels(outcome, question)
  const won = outcome.resolution_display

  if (position === constants.position.LONG) {
    return won === outcome.long_label || won === labels.longLabel
  }
  if (position === constants.position.SHORT) {
    return won === outcome.short_label || won === labels.shortLabel
  }

  return false
}

/**
 * Check if outcome is in winner state
 */
export const isWinnerState = (outcome, question, parentQuestion) => {
  const q = question || parentQuestion

  if (!q || q.resolution == null) {
    return false
  }

  if (outcome?._virtualPosition) {
    return sideMatchesResolutionDisplay(outcome, q, outcome._virtualPosition)
  }

  return q.resolution === outcome.id
}

/**
 * Check if outcome is in wrong prediction state
 */
export const isWrongPredictionState = (outcome, prediction, question, parentQuestion) => {
  const q = question || parentQuestion

  if (!prediction || !q) {
    return false
  }

  if (q.resolution && q.resolution !== outcome.id && outcome.id === prediction.outcome.id) {
    return true
  }

  return false
}

/**
 * Check if outcome is in prediction state
 */
export const isPredictionState = (outcome, prediction, question, parentQuestion) => {
  const q = question || parentQuestion

  if (prediction && q.resolution && prediction.outcome.id !== q.resolution) {
    return false
  }

  if (BetRuleService.isSold(prediction)) {
    return false
  }

  if (prediction && prediction.outcome && prediction.outcome.id === outcome.id) {
    return true
  }

  return false
}

/**
 * Check if outcome should be disabled
 */
export const isDisabledState = (outcome, prediction, question, parentQuestion) => {
  const q = question || parentQuestion

  // Don't disable if market is resolved
  if (QuestionService.isResolved(q)) {
    return false
  }

  // Don't disable if market is paused/reviewing
  if (QuestionService.isPaused(q)) {
    return false
  }

  // Don't disable if market is pending (waiting for results)
  if (QuestionService.isWaitingForResults(q)) {
    return false
  }

  // Don't disable if user has a prediction/position
  if (
    isPredictionState(outcome, prediction, question, parentQuestion) ||
    isWinnerState(outcome, question, parentQuestion) ||
    isWrongPredictionState(outcome, prediction, question, parentQuestion)
  ) {
    return false
  }

  // Don't disable if market is wagerable
  if (q.is_wagerable && !isPredictionState(outcome, prediction, question, parentQuestion)) {
    return false
  }

  // Otherwise, disable (cancelled, closed markets)
  return true
}

